/**
 * Racks (claude-docs/2026-10-30-venus-full-line-design.md, group 5): open
 * wall units an upper bay may be instead of a cabinet — the one rule the
 * panel, the fill, the front layout, the drawing and the bill read.
 *
 *   - `wine-rack` — Venus `WR3015` / `WR3615`, "Wall - Wine Rack", 30" or
 *     36" wide, 15" tall: a diamond lattice the bottles lie in (catalog p.15);
 *   - `wine-x` — `WR1515` / `WR1818`, "Wall - Wine Rack - incl. Shelf", 15"
 *     or 18" square: two boards corner to corner (p.16);
 *   - `plate-rack` — `PR3015`, "Wall - Plate Rack", 30" x 15": 11 plate
 *     slots between dowels (p.16);
 *   - `wine-cubes` — `WC0630` / `WC0636` / `WC0642`, "Wall - Wine Cube", 6"
 *     wide, 30" / 36" / 42" tall: 5 / 6 / 7 cubes stacked (p.16).
 *
 * Each is 12" deep and hangs from the uppers' top line like any upper (my
 * call): a 15" one leaves the wall open under it, as a bridge cabinet does;
 * the wine cubes stand the uppers' own height. A generic line offers the
 * same sizes.
 *
 * Pure; metres.
 */
const { CABINET } = require('./cabinetFrontSpecs');

const IN = 0.0254;
const TOL_IN = 1 / 16;
// a slat, a dowel: how thick each is
const SLAT_M = 0.5 * IN;
// the lattice's diamonds, measured up the box: a bottle's width and a bit
const LATTICE_PITCH_M = 4.5 * IN;
// the plate rack's dowels stand in two rows: in from the front, and the back
const DOWEL_INSET_M = 1.5 * IN;

/** Each rack: the widths and heights it comes in, and how it is drawn. */
const RACKS = Object.freeze({
  'wine-rack': Object.freeze({
    widthsIn: Object.freeze([30, 36]),
    heightIn: 15,
    pattern: 'lattice',
    name: 'lattice wine rack',
    category: 'wineRack',
    kind: 'wine-rack',
  }),
  'wine-x': Object.freeze({
    widthsIn: Object.freeze([15, 18]),
    square: true,
    pattern: 'x',
    name: 'wine rack with an X',
    category: 'wineXRack',
    kind: 'wine-x-rack',
  }),
  'plate-rack': Object.freeze({
    widthsIn: Object.freeze([30]),
    heightIn: 15,
    pattern: 'plates',
    slots: 11,
    name: 'plate rack',
    category: 'plateRack',
    kind: 'plate-rack',
  }),
  'wine-cubes': Object.freeze({
    widthsIn: Object.freeze([6]),
    heightsIn: Object.freeze([30, 36, 42]),
    pattern: 'cubes',
    name: 'wine cube',
    category: 'wineCubes',
    kind: 'wine-cubes',
  }),
});

/** The rack front a built upper is, or null. */
const rackOf = (o) =>
  Object.keys(RACKS).find((id) => o?.category?.[RACKS[id].category]) ?? null;

/** Is this upper a rack? */
const isRack = (o) => rackOf(o) !== null;

const said = (m) => `${Math.round((m / IN) * 8) / 8}"`;
const listText = (xs) =>
  xs.length > 1
    ? `${xs.slice(0, -1).join(', ')} or ${xs[xs.length - 1]}`
    : xs.join('');
const near = (a, b) => Math.abs(a - b) <= TOL_IN;
const articled = (name) => `${/^[aeiou]/.test(name) ? 'An' : 'A'} ${name}`;

/**
 * How tall a rack `frontId` is built `widthIn` wide where the uppers' band
 * is `bandHeightIn` tall: its own height (a square one as tall as it is
 * wide), or for the wine cubes the tallest they come in that fits the band.
 * Null when none fits.
 */
function rackHeightIn(frontId, widthIn, bandHeightIn) {
  const spec = RACKS[frontId];
  if (!spec) return null;
  const fits = (h) => h <= bandHeightIn + 0.5;
  if (spec.heightsIn) {
    const fit = spec.heightsIn.filter(fits);
    return fit.length ? fit[fit.length - 1] : null;
  }
  const h = spec.square ? widthIn : spec.heightIn;
  return fits(h) ? h : null;
}

/**
 * Why this upper bay cannot be the rack `frontId`, or null when it can (or
 * the front is no rack): the widths it comes in, and the room the bay has —
 * a 15" rack in any upper at least that tall, the wine cubes only at a
 * height they come in.
 */
function rackReason(run, cabinet, frontId) {
  const spec = RACKS[frontId];
  const d = cabinet?.dimensions;
  if (!spec || !(d?.x > 0)) return null;
  const w = d.x / IN;
  const width = spec.widthsIn.find((x) => near(x, w));
  if (width == null)
    return `${articled(spec.name)} is ${listText(spec.widthsIn.map((x) => `${x}"`))} wide: this bay is ${said(d.x)}`;
  if (!(d.y > 0)) return null;
  const h = d.y / IN;
  if (spec.heightsIn) {
    if (spec.heightsIn.some((x) => Math.abs(x - h) <= 0.5)) return null;
    return `${articled(spec.name)} is ${listText(spec.heightsIn.map((x) => `${x}"`))} tall: this bay is ${said(d.y)}`;
  }
  const need = spec.square ? width : spec.heightIn;
  if (h + 0.5 >= need) return null;
  return `${articled(spec.name)} is ${need}" tall: this bay has ${said(d.y)}`;
}

/** The width a rack comes in nearest this bay's, metres. */
function rackWidthM(frontId, cabinet) {
  const spec = RACKS[frontId];
  if (!spec) return null;
  const now = (cabinet?.dimensions?.x ?? 0) / IN;
  const w = spec.widthsIn.reduce((a, b) =>
    Math.abs(b - now) < Math.abs(a - now) ? b : a
  );
  return w * IN;
}

const pad2 = (n) => String(Math.round(n)).padStart(2, '0');

/** A built rack's part number on a Venus line: WR3015, WR1515, PR3015, WC0636. */
function rackSkuOf(obj) {
  const frontId = rackOf(obj);
  if (!frontId) return null;
  const w = Math.round((obj.dimensions?.x ?? 0) / IN);
  const h = Math.round((obj.dimensions?.y ?? 0) / IN);
  if (frontId === 'wine-rack') return `WR${pad2(w)}${pad2(h)}`;
  if (frontId === 'wine-x') return `WR${pad2(w)}${pad2(w)}`;
  if (frontId === 'plate-rack') return `PR${pad2(w)}${pad2(h)}`;
  return `WC${pad2(w)}${pad2(h)}`;
}

/** How many cubes a wine-cube column this tall holds: 5 / 6 / 7. */
const wineCubeCount = (heightIn) => Math.round(heightIn / 6);

/**
 * The lattice's slats in the box's clear face, `w` x `h` about its centre
 * `(cx, cy)`: lines at 45° each way, one every `LATTICE_PITCH_M` up the box,
 * each clipped to the face.
 */
function latticeSlats(w, h, cx, cy) {
  const slats = [];
  const reach = w / 2 + h / 2;
  const n = Math.floor(reach / LATTICE_PITCH_M);
  for (const dir of [1, -1]) {
    for (let k = -n; k <= n; k += 1) {
      const c = k * LATTICE_PITCH_M;
      // the line y = dir·x + c, clipped to |x| ≤ w/2, |y| ≤ h/2
      const x0 = Math.max(-w / 2, dir > 0 ? -h / 2 - c : c - h / 2);
      const x1 = Math.min(w / 2, dir > 0 ? h / 2 - c : c + h / 2);
      const length = (x1 - x0) * Math.SQRT2;
      if (length <= SLAT_M * 2) continue;
      const x = (x0 + x1) / 2;
      slats.push({
        name: `rack-slat-${dir > 0 ? 'up' : 'down'}-${k + n}`,
        x: cx + x,
        y: cy + dir * x + c,
        length,
        thick: SLAT_M,
        angle: (dir * Math.PI) / 4,
      });
    }
  }
  return slats;
}

/**
 * What a rack holds inside its box, as seen from the front in the layout's
 * centred metres: `boards` like an open shelf's (the wine cubes' dividers),
 * and `bars` — slats and dowels, each `{ name, x, y, length, thick, angle }`
 * (radians from level, in the face) and, for a dowel, its own `depth` and
 * `z` (else the box's depth). Every surface that draws a rack draws THESE.
 *
 * @param {string} frontId
 * @param {{ width, bodyHeight, bodyCenterY, depth }} box
 */
function rackPieces(frontId, { width, bodyHeight, bodyCenterY, depth }) {
  const spec = RACKS[frontId];
  if (!spec) return { boards: [], bars: [] };
  const t = CABINET.panelThick;
  const w = width - 2 * t;
  const h = bodyHeight - 2 * t;
  const cy = bodyCenterY;
  if (spec.pattern === 'cubes') {
    const n = wineCubeCount(bodyHeight / IN);
    const boards = Array.from({ length: Math.max(0, n - 1) }, (_, i) => ({
      name: `rack-cube-${i}`,
      x: 0,
      y: cy - h / 2 + (h * (i + 1)) / n,
      width: w,
      height: t,
    }));
    return { boards, bars: [] };
  }
  if (spec.pattern === 'lattice')
    return { boards: [], bars: latticeSlats(w, h, 0, cy) };
  if (spec.pattern === 'x') {
    const angle = Math.atan2(h, w);
    const length = Math.hypot(w, h) - t;
    return {
      boards: [],
      bars: [angle, -angle].map((a, i) => ({
        name: `rack-x-${i}`,
        x: 0,
        y: cy,
        length,
        thick: t,
        angle: a,
      })),
    };
  }
  // plates: a dowel either side of each slot, front and back
  const count = spec.slots + 1;
  const bars = [];
  for (const [row, z] of [
    ['front', depth / 2 - DOWEL_INSET_M],
    ['back', -depth / 2 + t + DOWEL_INSET_M],
  ]) {
    for (let i = 0; i < count; i += 1) {
      bars.push({
        name: `rack-dowel-${row}-${i}`,
        x: -w / 2 + SLAT_M / 2 + ((w - SLAT_M) * i) / (count - 1),
        y: cy,
        length: h,
        thick: SLAT_M,
        angle: Math.PI / 2,
        depth: SLAT_M,
        z,
      });
    }
  }
  return { boards: [], bars };
}

module.exports = {
  RACKS,
  rackOf,
  isRack,
  rackHeightIn,
  rackReason,
  rackWidthM,
  rackSkuOf,
  wineCubeCount,
  rackPieces,
};
